import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, BookOpen, ListChecks, Compass } from "lucide-react";

/**
 * /free-guide — top-of-funnel lead magnet landing page.
 *
 * Lighter commitment than the Module 1 preview (no course access, just a
 * 7-page reading). Designed to capture email + kick off its own follow-up
 * sequence that points back toward the $997 course over two weeks.
 */
export default function FreeGuide() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!form.email.trim()) return toast.error("Email is required");
    setLoading(true);
    try {
      await api.post("/free-guide/signup", {
        name: form.name || undefined,
        email: form.email,
      });
      // Send them straight to the manual — instant gratification beats
      // "check your email" 100% of the time. The email lands too.
      toast.success("Sent to your inbox. Opening now.");
      navigate("/field-manual");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      {/* HERO */}
      <section className="bg-brand-ink text-white">
        <SEO
          title="Free: The Forged in Fire Field Manual"
          description="A free seven-page primer on how leaders are actually formed — by the moments they don't ask for, and the work they do the week before."
        />
        <div className="container-narrow py-24 md:py-32 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="overline text-brand-rust">A free field manual from Robbie South</p>
            <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[1.02]" data-testid="free-guide-headline">
              The leader you become<br />
              <em className="italic text-brand-rust">in the fire.</em>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/80 max-w-xl leading-relaxed">
              A seven-page primer on how leaders are actually formed — by the moments they don&apos;t ask for, and the work they do in the week <em>before</em> those moments arrive.
            </p>

            <ul className="mt-10 space-y-4 text-white/90">
              {[
                "The three things pressure reveals about a leader, in the order they show up",
                "Three weekly drills that compound over ninety days",
                "Five questions to ask yourself before the next hard call",
                "A thirty-day starter plan you can begin this Friday",
              ].map((l, i) => (
                <li key={i} className="flex gap-3 items-start" data-testid={`free-guide-benefit-${i}`}>
                  <CheckCircle2 className="w-5 h-5 text-brand-rust flex-shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{l}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Signup card */}
          <div className="lg:col-span-5">
            <div className="bg-white text-brand-ink p-8 md:p-10 border border-white/10 shadow-2xl">
              <p className="overline text-brand-rust">Sent to your inbox</p>
              <h2 className="font-display text-3xl mt-3 leading-tight">
                Read it in twenty minutes.<br />
                <em className="italic">Use it for years.</em>
              </h2>
              <p className="mt-4 text-brand-inkMuted text-sm leading-relaxed">
                Free. No tricks. You&apos;ll also get an occasional Field Note from Robbie — unsubscribe any time.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="free-guide-form">
                <input
                  placeholder="First name (optional)"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border-b border-brand-line py-3 text-brand-ink focus:outline-none focus:border-brand-rust"
                  data-testid="free-guide-name"
                />
                <input
                  required
                  type="email"
                  placeholder="Your email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-transparent border-b border-brand-line py-3 text-brand-ink focus:outline-none focus:border-brand-rust"
                  data-testid="free-guide-email"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="free-guide-submit"
                  className="w-full bg-brand-ink text-white hover:bg-black px-7 py-4 text-sm font-medium tracking-wide transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send me the field manual"}
                </button>
              </form>
              <p className="mt-6 text-xs text-brand-inkMuted">
                Already a subscriber?{" "}
                <Link to="/field-manual" className="text-brand-rust hover:text-brand-ink underline underline-offset-4">Skip the form &rarr;</Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="container-narrow py-20 md:py-28">
        <p className="overline text-brand-rust text-center">What&apos;s inside</p>
        <h2 className="font-display text-4xl md:text-5xl mt-3 text-brand-ink text-center max-w-3xl mx-auto leading-tight">
          A small set of things I would tell my younger self.
        </h2>
        <div className="grid md:grid-cols-3 gap-8 mt-16">
          {[
            { icon: BookOpen, title: "Section 1 — The three reveals", body: "What pressure shows about a leader, in the order it shows up. From the field, not from the classroom." },
            { icon: ListChecks, title: "Section 2 — The three drills", body: "The Friday written review. The weekly uncomfortable conversation. The decision made faster than feels comfortable." },
            { icon: Compass, title: "Section 3 + 4 — The work", body: "Five questions to ask before the next hard call, plus a four-week starter plan you can begin this Friday." },
          ].map((s, i) => (
            <article key={i} className="bg-brand-bg border border-brand-line p-8" data-testid={`free-guide-section-${i}`}>
              <s.icon className="w-7 h-7 text-brand-rust" />
              <h3 className="font-serif text-xl text-brand-ink mt-5 leading-snug">{s.title}</h3>
              <p className="mt-3 text-brand-ink leading-relaxed text-[15px]">{s.body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* SOCIAL PROOF / AUTHORITY */}
      <section className="bg-brand-bg border-t border-brand-line py-20">
        <div className="container-narrow max-w-3xl text-center">
          <p className="overline text-brand-rust">Why this, why Robbie</p>
          <blockquote className="font-display text-3xl md:text-4xl mt-6 text-brand-ink leading-tight">
            &ldquo;I spent the better part of two decades watching leaders fail in two specific ways. This is the shortest, most honest record of what I learned standing in those rooms.&rdquo;
          </blockquote>
          <p className="mt-8 overline text-brand-inkMuted">— Robbie South</p>
        </div>
      </section>
    </Layout>
  );
}
